/**
 * @author Gananath Athukorala
 * @version 0.1
 * @since 7/7/2023
 */
import React, { useState, useEffect, useRef } from "react"
import labelsEN from "../../../../locales/en/translation.json"
import CreateTerminalsModal from "./components/CreateTerminalsModal"
import BulkImportTerminalsModal from "./components/BulkImportTerminalsModal"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { Fab, TextField, Button, Box,Tooltip, MenuItem, Alert, FormHelperText } from "@mui/material"
import { Tabs, Tab, Badge } from "react-bootstrap"
import AddIcon from "@mui/icons-material/Add"
import ApprovedTerminals from "./components/ApprovedTerminals"
import PendingTerminals from "./components/PendingTerminals"
import OrgsDropdown from "../../components/orgsDropdown/OrgsDropdown"
import GroupsDropdown from "../../components/groupsDropdown/GroupsDropdown"
import FilterByDropdown from "../../components/filterByDropdown/FilterByDropdown"
import { useLocation } from "react-router-dom"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import ManageHistoryIcon from "@mui/icons-material/ManageHistory"
import TerminalHistoryModal from "./components/TerminalHistoryModal"
import LtsAppStatusDropdown from "../../components/ltsAppStatusDropdown/LtsAppStatusDropdown"
import TerminalRegistrationView from "./components/TerminalRegistrationView"
import { filterGridSx, filterActionsSx } from "../../components/filterLayout"
import AppIDDropdown from "../../components/appIDDropdown/AppIDDropdown"
import { isFilterReady } from "../../components/filterByDropdown/filterValidation"

const Terminals = () => {
  const selectOrgRef = useRef(null)
  const selectGrpRef = useRef(null)
  const selectFilterTypeRef = useRef(null)
  const selectAppStatusRef = useRef(null)

  const AUTO_APPROVE = localStorage.getItem("SET_AUTO_APPROVE")

  const [pendingCount, setPendingCount] = useState(0)
  const [updateData,setUpdateData] = useState(false)

  //modals state
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkModalInitialized, setBulkModalInitialized] = useState(false)
  const [historyModalOpen,setHistoryModalOpen] = useState(false) 

  const [orgCode, setOrgCode] = useState()
  const [grpId, setGrpId] = useState()
  const [terminalSerial, setTerminalSerial] = useState()
  const [ltsAppStatus, setLtsAppStatus] = useState()
  const [applicationId, setApplicationId] = useState("")
  const [applicationIdError, setApplicationIdError] = useState(false)

  const [filterByType, setFilterByType] = useState()
  const [filterByVal, setFilterByVal] = useState()
  const [isOrgDisabled, setIsOrgDisabled] = useState(true)
  const [isGrpDisabled, setIsGrpDisabled] = useState(true)
  const [istrmnlDisabled, setIsTrmnlDisabled] = useState(true)
  const [isSearch, setIsSearch] = useState(false)
  const [terminalView, setTerminalView] = useState("registered")
  const [registrationQuery, setRegistrationQuery] = useState(null)
  const [searchError, setSearchError] = useState("")
  const unassigned = terminalView === "unassigned"
  const registrationView = terminalView !== "registered"
  const searchValue = filterByType === "F_ORG_CODE" ? orgCode : filterByType === "F_GRP_ID" ? grpId : terminalSerial?.trim()

  const handleViewChange = (event) => {
    const view = event.target.value
    setTerminalView(view)
    setRegistrationQuery(null)
    setSearchError("")
    setIsSearch(false)
    setUpdateData(false)
    setLtsAppStatus(undefined)
    setApplicationId("")
    setApplicationIdError(false)
    if (view === "unassigned" || !filterByType) {
      setFilterByType("F_ORG_CODE")
      setFilterByVal(orgCode)
      setIsOrgDisabled(false)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(true)
      setGrpId(undefined)
      setTerminalSerial("")
    }
  }

  // Show/Hide Dropdown
  const location = useLocation()
  useEffect(() => {
    if (location.state) {
      setTerminalView("registered")
      setRegistrationQuery(null)
      const fType = location.state.filterByType
      setOrgCode(location.state.orgCode)
      setGrpId(location.state.groupId)
      setFilterByVal(location.state.groupId)
      setFilterByType(fType)
      if (fType === "F_GRP_ID") {
        setIsOrgDisabled(false)
        setIsGrpDisabled(false)
        setIsTrmnlDisabled(true)
        // Apply the group-card navigation filters and search in the same render.
        setTerminalSerial("")
        setLtsAppStatus(undefined)
        setApplicationId("")
        setApplicationIdError(false)
        setIsSearch(Boolean(location.state.orgCode && location.state.groupId != null))
      } else {
        setIsOrgDisabled(true)
        setIsGrpDisabled(true)
        setIsTrmnlDisabled(true)
      }
    }
  }, [location.state, location.key])

  const handleFilterByChange = (e) => {
    selectOrgRef.current?.clearValue()
    selectGrpRef.current?.clearValue()
    setOrgCode(undefined)
    setApplicationId("")
    setApplicationIdError(false)
    setGrpId(undefined)
    setTerminalSerial("")
    setFilterByVal(undefined)
    setSearchError("")
    const value = e?.value
    setFilterByType(value)
    if (value === "F_ORG_CODE") {
      setIsOrgDisabled(false)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(true)
    } else if (value === "F_GRP_ID") {
      setIsOrgDisabled(false)
      setIsGrpDisabled(false)
      setIsTrmnlDisabled(true)
    } else if (value === "F_TRMNL_SERIAL") {
      setIsOrgDisabled(true)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(false)
    } else {
      setIsOrgDisabled(true)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(true)
    }
  }

const handleAppStatusChange = (e) => {
    const value = e?.value
    setLtsAppStatus(value)
    setApplicationIdError(false)
    if (!value) setApplicationId("")
  }

  const handleOrgChange = (event) => {
    const value = event?.value
    setFilterByVal(filterByType === "F_ORG_CODE" ? value : undefined)
    setOrgCode(value)
    setApplicationId("")
    setApplicationIdError(false)
    setGrpId(undefined)
  }
  const handleGrpChange = (event) => {
    const value = event?.value
    setFilterByVal(value)
    setGrpId(value)
  }

  const handleTerminalSerialChange = (e) => {
    const value = e.target.value
    setFilterByVal(value)
    setTerminalSerial(value)
  }
  const clearFilters = () => {
    selectOrgRef.current?.clearValue()
    selectGrpRef.current?.clearValue()
    selectFilterTypeRef.current?.clearValue()
    selectAppStatusRef.current?.clearValue()
    setOrgCode(undefined)
    setGrpId(undefined)
    setFilterByVal(undefined)
    setFilterByType(unassigned ? "F_ORG_CODE" : undefined)
    setIsOrgDisabled(!unassigned)
    setIsGrpDisabled(true)
    setIsTrmnlDisabled(true)
    setRegistrationQuery(null)
    setSearchError("")
    setTerminalSerial("")
    setLtsAppStatus(undefined)
    setApplicationId("")
    setApplicationIdError(false)
    setIsSearch(false)
  }

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ flexWrap: "wrap", gap: 2 }}>
        <h1>{labelsEN.app_terminal_page_title}</h1>
        <Box display="flex" justifyContent="flex-end" sx={{ gap: 2, flexWrap: "wrap" }}>
          <Fab color="primary" variant="extended" onClick={() => {
            setBulkModalInitialized(true)
            setBulkModalOpen(true)
          }}>
            <UploadFileIcon sx={{ mr: 1 }} />
            Bulk import
          </Fab>
          <Fab
            onClick={() => setCreateModalOpen(true)}
            color="success"
            variant="extended"
          >
            <AddIcon sx={{ mr: 1 }} />
            Create new
          </Fab>
          <Tooltip arrow placement="bottom" title="View terminal app history">
            <Fab
              onClick={() => setHistoryModalOpen(true)}
              color="info"
              variant="circular"
              size="small"
              sx={{ marginTop: "3px" }}
            >
              <ManageHistoryIcon />
            </Fab>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ ...filterGridSx, mt: 3 }}>
        <TextField select label="Terminal view" value={terminalView} onChange={handleViewChange}
          sx={{ minWidth: 280, maxWidth: "100%" }}>
          <MenuItem value="registered">Registered terminals</MenuItem>
          <MenuItem value="unassigned">Unassigned terminals</MenuItem>
          <MenuItem value="bulkPending">Pending bulk registration</MenuItem>
        </TextField>
      </Box>
        {registrationView && <Alert severity="info" sx={{ mt: 2 }}>
          {unassigned ? "Terminals without a group. Select an organization to find terminals and assign them to a group within that organization."
            : "Terminals imported in bulk that are still waiting to register. Search by organization, group, or terminal serial. This is separate from pending approval."}
        </Alert>}
      <Box sx={{ ...filterGridSx, mt: 3 }}>
        {!unassigned && <FilterByDropdown
          onChange={handleFilterByChange}
          selectRef={selectFilterTypeRef}
          value={filterByType}
        />}

         <OrgsDropdown
          orgCode={orgCode ? orgCode : ""}
          onChange={handleOrgChange}
          isOrgDisabled={isOrgDisabled}
          selectRef={selectOrgRef}
        />
        {!unassigned && <GroupsDropdown
          key={orgCode || "no-org"}
          orgCode={orgCode ? orgCode : ""}
          groupId={grpId ? grpId : ""}
          onChange={handleGrpChange}
          isGrpDisabled={isGrpDisabled || !orgCode}
          selectRef={selectGrpRef}
        />}

        {!istrmnlDisabled && <TextField
          name="terminalSerial"
          value={terminalSerial || ""}
          type="text"
          variant="outlined"
          color="secondary"
          label="Terminal Serial"
          onChange={handleTerminalSerialChange}
          disabled={istrmnlDisabled}
        />}

        {!registrationView && <LtsAppStatusDropdown
          onChange={handleAppStatusChange}
          selectRef={selectAppStatusRef}
          value={ltsAppStatus}
        />}
        {ltsAppStatus && <Box><AppIDDropdown
          key={orgCode || "all-orgs"}
          orgCode={orgCode || ""}
          value={applicationId}
          error={applicationIdError}
          onChange={(app) => {
            setApplicationId(String(app?.applicationId ?? ""))
            setApplicationIdError(false)
          }}
        /><FormHelperText error={applicationIdError}>
          {applicationIdError ? "Select an application to search by LTS app status." : "Required when an LTS app status is selected."}
        </FormHelperText></Box>}

      </Box>
      <Box sx={filterActionsSx}>
        <Button
          variant="contained"
          color="warning"
          startIcon={<SearchIcon />}
          disabled={!isFilterReady(unassigned ? "F_ORG_CODE" : filterByType, orgCode, grpId, terminalSerial)}
          onClick={() => {
            setSearchError("")
            if (registrationView) {
              if (!filterByType || !searchValue || (filterByType === "F_GRP_ID" && !orgCode)) {
                setSearchError(unassigned ? "Select an organization to search unassigned terminals." : "Choose a filter and enter or select its value before searching.")
                return
              }
              setRegistrationQuery({ filterType: filterByType, value: searchValue, id: Date.now() })
              return
            }
            if (ltsAppStatus && !applicationId.trim()) {
              setApplicationIdError(true)
              return
            }
            setIsSearch(true)
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ClearIcon />}
          onClick={clearFilters}
        >
          Clear
        </Button>
      </Box>
      {searchError && <Alert severity="warning">{searchError}</Alert>}
      <div>
        {registrationView ? <TerminalRegistrationView key={`${terminalView}:${registrationQuery?.id || "empty"}`}
          query={registrationQuery} unassigned={unassigned} refresh={updateData} onRefresh={setUpdateData} /> : <Tabs
          defaultActiveKey="Approved"
          id="uncontrolled-tab-example"
          className="mb-3"
        >
          <Tab eventKey="Approved" title={"Approved"}>
            <ApprovedTerminals
              grpId={grpId}
              orgCode={orgCode}
              terminalSerial={terminalSerial}
              isSearch={isSearch}
              setIsSearch={setIsSearch}
              filterType={filterByType}
              filterByVal={filterByVal}
              updateData={updateData}
              setUpdateData={setUpdateData}
              ltsAppStatus={ltsAppStatus}
              applicationId={applicationId}
            />
          </Tab>
          {AUTO_APPROVE === "false" && (
            <Tab
              eventKey="Pending"
              title={
                <div>
                  Pending approval{" "}
                  {pendingCount !== 0 && (
                    <Badge bg="danger">{pendingCount}</Badge>
                  )}
                </div>
              }
            >
              <PendingTerminals
                updateData={updateData}
                setUpdateData={setUpdateData}
                setPendingCount={setPendingCount}
                grpId={grpId}
                isSearch={isSearch}
                setIsSearch={setIsSearch}
                filterType={filterByType}
                filterByVal={filterByVal}
              />
            </Tab>
          )}
        </Tabs>}
      </div>

      <CreateTerminalsModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        setUpdateData={setUpdateData}
        orgCode={orgCode}
        grpId={grpId}
      />
      {bulkModalInitialized && <BulkImportTerminalsModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        setUpdateData={setUpdateData}
        orgCode={orgCode}
        grpId={grpId}
      />}
      <TerminalHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
      />
    </div>
  )
}
export default Terminals
